import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin, Subscription, debounceTime } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { LiveRefreshService } from '@/services/live-refresh.service';
import { AuthService } from '@/core/services/auth-service';
import { PAYMENT_METHOD_LABELS } from '@/utils/reservation-status';
import { JalaliDatepickerComponent } from '@/shared/jalali-datepicker';

import { Room } from '@/model/room.model';
import { Reservation, ReservationStatus } from '@/model/reservation.model';
import { RoomsService } from '@/services/rooms.service';
import { ReservationsService } from '@/services/reservations.service';
import { DASHBOARD_PRESETS, DashboardPreset, DashboardRangeService } from '@/services/dashboard-range.service';
import { LayoutService } from '@/layout/service/layout.service';
import {
  BUSINESS_DAY_END_MINUTES,
  BUSINESS_DAY_START_MINUTES,
  businessDateOf,
  currentBusinessDate,
  formatJalaliLong,
  toApiDateKey,
} from '@/utils/business-day';
import { formatJalali, fromApiDateKey } from '@/utils/jalali';
import { capacityLabel } from '@/utils/room';
import { LocationsService } from '@/services/locations.service';

// ساعت‌های روز به ترتیب: ۰ تا ۲۳
const BUSINESS_HOURS: number[] = Array.from(
  { length: (24 * 60 - BUSINESS_DAY_START_MINUTES + BUSINESS_DAY_END_MINUTES) / 60 },
  (_, i) => (BUSINESS_DAY_START_MINUTES / 60 + i) % 24,
);
const PEAK_HOURS_COUNT = 3;
// همسان با بک‌اند: ثبت ورود از ۳۰ دقیقه پیش از شروع رزرو
const CHECK_IN_EARLY_MINUTES = 30;
// ۱۵ دقیقه‌ی پایانی رزرو = «در انتظار خروج»
const LEAVING_MINUTES = 15;
const MAX_LIST_ROWS = 50;

import { BrandingService } from '@/services/branding.service';
import { STATUS_LABELS, STATUS_ORDER, STATUS_SEVERITY, isRevenue } from '@/utils/reservation-status';

const STATUS_ICONS: Record<ReservationStatus, string> = {
  pending: 'pi pi-hourglass',
  paid: 'pi pi-wallet',
  confirmed: 'pi pi-check-circle',
  cancelled: 'pi pi-times-circle',
  completed: 'pi pi-flag',
};

interface StatCard {
  title: string;
  value: string;
  hint: string;
  icon: string;
}

interface RoomStatus {
  room: Room;
  busyNow: boolean;
  text: string;
}

interface RoomBreakdown {
  room: Room;
  count: number;
  revenue: number;
  occupancy: number;
}

const faNumber = (n: number) => n.toLocaleString('fa-IR');
const faMoney = (n: number) => `${faNumber(n)} تومان`;
// ساعت همیشه ۲۴ ساعته
const faTime = (d: Date) =>
  d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', hour12: false });
const hourLabel = (h: number) => `${faNumber(h)}:۰۰`;
const dayCount = (from: string, to: string) =>
  Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000) + 1;

@Component({
  selector: 'app-cafe-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, ChartModule, DialogModule, SelectButtonModule, TagModule, TooltipModule, ToastModule, JalaliDatepickerComponent],
  providers: [MessageService],
  template: `
    <p-toast />
    <div class="cafe-dash">
      <!-- سربرگ -->
      <section class="cafe-hero">
        <div>
          <div class="cafe-hero-title">{{ branding.branding()?.appName }}</div>
          <div class="cafe-hero-date">
            <i class="pi pi-calendar"></i>
            امروز {{ todayLabel }}
          </div>
        </div>
        <div class="cafe-hero-actions">
          <p-button label="رزرو جدید" icon="pi pi-plus" routerLink="/reservations/new" styleClass="cafe-btn-gold" />
          <p-button label="وضعیت اتاق‌ها" icon="pi pi-th-large" routerLink="/room-availability" [outlined]="true" styleClass="cafe-btn-outline" />
        </div>
      </section>

      <!-- رزروهای ساعات پیش رو -->
      <section class="cafe-card cafe-upcoming">
        <div class="cafe-upcoming-head">
          <span class="font-bold"><i class="pi pi-clock ml-2 text-primary"></i>رزروهای ساعات پیش رو</span>
          <small class="text-surface-500">{{ upcoming.length.toLocaleString('fa-IR') }} رزرو</small>
        </div>
        <div *ngIf="!upcoming.length" class="text-surface-500 text-center py-3">رزروی برای ساعات پیش رو ثبت نشده است.</div>
        <div class="cafe-upcoming-list">
          <div class="cafe-upcoming-item" *ngFor="let r of upcoming; trackBy: trackById" [attr.data-stage]="stageOf(r)" (click)="openDetails(r)">
            <div class="cafe-upcoming-row">
              <div class="cafe-upcoming-main">
                <span class="stage-tag" [attr.data-stage]="stageOf(r)">{{ stageLabel(r) }}</span>
                <span class="name">{{ customerOf(r) }}</span>
                <span class="sep">|</span>
                <span class="code">کد {{ r.id }}</span>
                <span class="sep">|</span>
                <span class="time">{{ timeOf(r.startTime) }} تا {{ timeOf(r.endTime) }}</span>
                <span class="day-tag" [class.today]="dayTag(r.startTime) === 'امروز'">{{ dayTag(r.startTime) }}</span>
                <span class="room">{{ r.room?.name || roomName(r.roomId) }}</span>
              </div>
              <div class="cafe-upcoming-actions" (click)="$event.stopPropagation()">
                <p-tag [value]="statusLabel(r.status)" [severity]="statusSeverity(r.status)" />
                <ng-container *ngIf="!r.checkedInAt">
                  <p-button
                    label="ثبت ورود"
                    icon="pi pi-sign-in"
                    size="small"
                    (onClick)="checkIn(r)"
                    [loading]="checkingId === r.id"
                    [disabled]="!canCheckIn || !checkInOpen(r)"
                    [pTooltip]="checkInOpen(r) ? '' : 'ثبت ورود از ' + checkInEarly + ' دقیقه پیش از شروع رزرو فعال می‌شود'"
                    tooltipPosition="top"
                  />
                </ng-container>
                <ng-container *ngIf="r.checkedInAt && !r.checkedOutAt">
                  <span class="checked-label"><i class="pi pi-check-circle"></i> ورود {{ timeOf(r.checkedInAt) }}</span>
                  <p-button label="ثبت خروج" icon="pi pi-sign-out" size="small" severity="secondary" [outlined]="true" (onClick)="checkOut(r)" [loading]="checkingId === r.id" [disabled]="!canCheckIn" />
                </ng-container>
              </div>
            </div>
            <!-- تایمر: تا شروع رزرو، و پس از شروع تا پایان (مثل شارژ باتری که با گذشت زمان خالی می‌شود) -->
            <div class="cafe-timer">
              <span class="cafe-timer-text">
                <i class="pi" [ngClass]="started(r) ? 'pi-hourglass' : 'pi-clock'"></i>
                {{ started(r) ? 'مانده تا پایان' : 'مانده تا شروع' }}
                <b dir="ltr">{{ countdown(r) }}</b>
              </span>
              <span class="cafe-battery" [attr.data-level]="batteryLevel(r)">
                <span class="cafe-battery-fill" [style.width.%]="battery(r)"></span>
              </span>
              <small class="cafe-battery-pct">{{ faNumber(battery(r)) }}٪</small>
            </div>
          </div>
        </div>
      </section>

      <!-- جزئیات رزرو و مشتری -->
      <p-dialog [(visible)]="detailsVisible" [modal]="true" [style]="{ width: '520px' }" [breakpoints]="{ '560px': '96vw' }" [header]="'رزرو ' + (details?.id ?? '')">
        <ng-template #content>
          <div *ngIf="details" class="cafe-details">
            <div><span>مشتری</span><b>{{ customerOf(details) }}</b></div>
            <div><span>شماره موبایل</span><b dir="ltr">{{ details.customerPhone }}</b></div>
            <div><span>اتاق</span><b>{{ details.room?.name }}</b></div>
            <div><span>تاریخ</span><b>{{ dayOf(details.startTime) }}</b></div>
            <div><span>ساعت</span><b dir="ltr">{{ timeOf(details.startTime) }} - {{ timeOf(details.endTime) }}</b></div>
            <div><span>تعداد نفرات</span><b>{{ details.guestCount ?? '-' }} نفر<span *ngIf="details.room?.capacity && details.guestCount && details.guestCount > details.room!.capacity" class="text-orange-600"> (بیش از ظرفیت)</span></b></div>
            <div><span>وضعیت</span><p-tag [value]="statusLabel(details.status)" [severity]="statusSeverity(details.status)" /></div>
            <div><span>پرداخت</span><b>{{ details.paymentStatus === 'paid' ? 'پرداخت‌شده' + (details.paymentMethod ? ' (' + methodLabel(details.paymentMethod) + ')' : '') : details.paymentStatus === 'refunded' ? 'برگشت داده شده' : 'پرداخت‌نشده' }}</b></div>
            <div><span>مبلغ</span><b>{{ faMoney(details.totalPrice ?? 0) }}</b></div>
            <div *ngIf="details.description"><span>توضیحات</span><b>{{ details.description }}</b></div>
            <div><span>ثبت</span><b>{{ details.isOnline ? 'آنلاین (مشتری)' : 'پنل' + (details.user ? ' - ' + details.user.firstName + ' ' + details.user.lastName : '') }}</b></div>
            <div><span>ورود مشتری</span><b>{{ details.checkedInAt ? 'ثبت شده - ' + timeOf(details.checkedInAt) : 'ثبت نشده' }}</b></div>
            <div *ngIf="details.checkedOutAt"><span>خروج مشتری</span><b>{{ timeOf(details.checkedOutAt) }}</b></div>
            <div><span>وضعیت حضور</span><b>{{ stageLabel(details) }} · {{ started(details) ? 'مانده تا پایان' : 'مانده تا شروع' }} {{ countdown(details) }}</b></div>
          </div>
        </ng-template>
        <ng-template #footer>
          <p-button *ngIf="details?.checkedInAt" label="لغو ثبت ورود" severity="secondary" text (onClick)="checkIn(details!, true)" [disabled]="!canCheckIn" />
          <p-button *ngIf="details?.checkedInAt && !details?.checkedOutAt" label="ثبت خروج" icon="pi pi-sign-out" severity="secondary" [outlined]="true" (onClick)="checkOut(details!)" [disabled]="!canCheckIn" />
          <p-button *ngIf="details && !details.checkedInAt" label="ثبت ورود" icon="pi pi-sign-in" (onClick)="checkIn(details)" [loading]="checkingId === details.id" [disabled]="!canCheckIn || !checkInOpen(details)" />
          <p-button label="لیست رزروها" icon="pi pi-list" [outlined]="true" routerLink="/reservations" (onClick)="detailsVisible = false" />
        </ng-template>
      </p-dialog>

      <!-- بازه‌ی گزارش -->
      <section class="cafe-card cafe-range">
        <div class="cafe-range-presets">
          <div class="cafe-presets" role="group" aria-label="بازه‌ی گزارش">
            <button
              type="button"
              *ngFor="let p of presets"
              class="cafe-preset"
              [class.active]="range().preset === p.value"
              [class.today]="p.value === 'today'"
              (click)="onPreset(p.value)"
            >
              {{ p.label }}
            </button>
          </div>
        </div>
        <div class="cafe-range-pick">
          <span class="font-bold">بازه انتخابی:</span>
          <span class="cafe-range-end">
            <span>از:</span>
            <app-jalali-datepicker
              [ngModel]="customFrom"
              (ngModelChange)="onCustomChange($event, null)"
              format="yyyy/MM/dd"
              cssClass="cafe-date"
            />
            <span class="cafe-range-text">{{ fromLabel }}</span>
          </span>
          <span class="cafe-range-end">
            <span>تا:</span>
            <app-jalali-datepicker
              [ngModel]="customTo"
              (ngModelChange)="onCustomChange(null, $event)"
              format="yyyy/MM/dd"
              cssClass="cafe-date"
            />
            <span class="cafe-range-text">{{ toLabel }}</span>
          </span>
        </div>
      </section>

      <div *ngIf="loading" class="cafe-card text-center text-surface-500">در حال بارگذاری...</div>

      <ng-container *ngIf="!loading">
        <!-- کارت‌های آمار بازه -->
        <section class="cafe-stats">
          <div class="cafe-card cafe-stat" *ngFor="let s of stats">
            <div class="cafe-stat-icon"><i [class]="s.icon"></i></div>
            <div>
              <div class="cafe-stat-title">{{ s.title }}</div>
              <div class="cafe-stat-value">{{ s.value }}</div>
              <div class="cafe-stat-hint">{{ s.hint }}</div>
            </div>
          </div>
        </section>

        <!-- تعداد وضعیت رزروها -->
        <section class="cafe-card">
          <div class="cafe-card-title mb-4">وضعیت رزروها</div>
          <div class="cafe-status-grid">
            <div class="cafe-status" *ngFor="let s of statusCounts" [attr.data-status]="s.status">
              <i [class]="s.icon"></i>
              <div>
                <div class="cafe-status-value">{{ faNumber(s.count) }}</div>
                <div class="cafe-status-label">{{ s.label }}</div>
              </div>
            </div>
          </div>
        </section>

        <div class="cafe-grid">
          <!-- ساعت‌های شلوغ -->
          <section class="cafe-card cafe-busy">
            <div class="cafe-card-head">
              <div>
                <div class="cafe-card-title">ساعت‌های شلوغ</div>
                <div class="cafe-card-sub">بر اساس رزروهای بازه‌ی انتخابی</div>
              </div>
            </div>
            <ng-container *ngIf="hasChartData; else noChart">
              <div class="cafe-peak">
                <i class="pi pi-bolt"></i>
                اوج شلوغی: <b>{{ peakLabel }}</b>
              </div>
              <div class="cafe-chart-wrap">
                <p-chart type="bar" [data]="chartData" [options]="chartOptions" height="100%" />
              </div>
              <div class="cafe-legend">
                <span><i class="cafe-swatch cafe-swatch-peak"></i> ساعت‌های اوج</span>
                <span><i class="cafe-swatch cafe-swatch-normal"></i> سایر ساعت‌ها</span>
              </div>
            </ng-container>
            <ng-template #noChart>
              <div class="text-surface-500 text-center py-12">در این بازه رزروی ثبت نشده است.</div>
            </ng-template>
          </section>

          <!-- وضعیت اتاق‌ها -->
          <section class="cafe-card cafe-rooms">
            <div class="cafe-card-title mb-4">وضعیت اتاق‌ها همین الان</div>
            <div *ngIf="!roomStatuses.length" class="text-surface-500">اتاق فعالی ثبت نشده است.</div>
            <div class="cafe-room" *ngFor="let r of roomStatuses">
              <span class="cafe-dot" [class.busy]="r.busyNow"></span>
              <div class="flex-1">
                <div class="font-bold">{{ r.room.name }}</div>
                <div class="text-sm text-surface-500">{{ r.text }}</div>
              </div>
              <span class="text-sm text-surface-500">{{ capacityLabel(r.room) }}</span>
            </div>
          </section>
        </div>

        <!-- عملکرد هر اتاق -->
        <section class="cafe-card">
          <div class="cafe-card-title mb-4">عملکرد اتاق‌ها در بازه‌ی انتخابی</div>
          <div class="overflow-x-auto">
            <table class="cafe-table">
              <thead>
                <tr>
                  <th>اتاق</th>
                  <th>تعداد رزرو</th>
                  <th>درآمد</th>
                  <th>ضریب اشغال</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let b of roomBreakdown">
                  <td class="font-bold">{{ b.room.name }}</td>
                  <td>{{ faNumber(b.count) }}</td>
                  <td>{{ faMoney(b.revenue) }}</td>
                  <td>
                    <div class="cafe-bar"><span [style.width.%]="b.occupancy"></span></div>
                    <small>{{ faNumber(b.occupancy) }}٪</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- رزروهای بازه -->
        <section class="cafe-card">
          <div class="cafe-card-head mb-4">
            <div class="cafe-card-title">رزروهای {{ isSingleDay ? rangeLabel : 'بازه‌ی انتخابی' }}</div>
            <p-button label="همه‌ی رزروها" icon="pi pi-arrow-left" [text]="true" routerLink="/reservations" />
          </div>
          <div *ngIf="!rangeReservations.length" class="text-surface-500 py-4 text-center">
            در این بازه رزروی ثبت نشده است.
          </div>
          <div class="cafe-timeline" *ngIf="rangeReservations.length">
            <div class="cafe-tl-row" *ngFor="let r of listedReservations" [class.now]="isNow(r)" [class.cancelled]="r.status === 'cancelled'">
              <div class="cafe-tl-time">
                <div *ngIf="!isSingleDay" class="text-xs font-normal">{{ dayLabel(r) }}</div>
                {{ timeRange(r) }}
              </div>
              <div class="flex-1">
                <div class="font-bold">{{ r.room?.name || roomName(r.roomId) }}</div>
                <div class="text-sm text-surface-500">{{ customerLabel(r) }}</div>
              </div>
              <span *ngIf="r.totalPrice != null" class="text-sm text-surface-500">{{ faMoney(r.totalPrice) }}</span>
              <span *ngIf="isNow(r)" class="cafe-now-badge">در حال برگزاری</span>
              <p-tag [value]="statusLabel(r.status)" [severity]="statusSeverity(r.status)" />
            </div>
            <div *ngIf="rangeReservations.length > listedReservations.length" class="text-center text-surface-500 text-sm pt-2">
              {{ faNumber(rangeReservations.length - listedReservations.length) }} رزرو دیگر در «لیست رزروها»
            </div>
          </div>
        </section>
      </ng-container>
    </div>

    <style>
      .cafe-dash {
        --cafe-green: #0e3b32;
        --cafe-green-deep: #0a2a24;
        --cafe-gold: #e9c98f;
        --cafe-gold-strong: #b8862b;
        --cafe-accent-bg: #f7efe1;
        --cafe-accent-ink: #8a6420;
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }
      :host-context(.app-dark) .cafe-dash {
        --cafe-accent-bg: rgba(233, 201, 143, 0.12);
        --cafe-accent-ink: #e9c98f;
      }
      .cafe-hero {
        background: linear-gradient(135deg, var(--cafe-green) 0%, var(--cafe-green-deep) 100%);
        color: var(--cafe-gold);
        border-radius: 18px;
        padding: 1.75rem 2rem;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1.25rem;
        position: relative;
        overflow: hidden;
      }
      .cafe-hero::after {
        content: '\\2615';
        position: absolute;
        left: 1.5rem;
        bottom: -1.5rem;
        font-size: 7rem;
        opacity: 0.07;
        pointer-events: none;
      }
      .cafe-upcoming-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.75rem;
      }
      .cafe-upcoming-list {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        max-height: 22rem;
        overflow-y: auto;
      }
      .cafe-upcoming-item {
        display: flex;
        flex-direction: column;
        gap: 0.45rem;
        padding: 0.65rem 0.85rem;
        border: 1px solid var(--surface-border);
        border-radius: 10px;
        cursor: pointer;
        transition: border-color 0.15s;
      }
      .cafe-upcoming-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem 1rem;
      }
      .cafe-upcoming-item:hover {
        border-color: var(--primary-color);
      }
      .cafe-upcoming-item[data-stage='in'] {
        background: rgba(34, 197, 94, 0.08);
      }
      .cafe-upcoming-item[data-stage='leaving'] {
        background: rgba(249, 115, 22, 0.08);
      }
      .stage-tag {
        font-size: 0.75rem;
        font-weight: 700;
        padding: 0.15rem 0.55rem;
        border-radius: 999px;
        background: rgba(234, 179, 8, 0.15);
        color: #a16207;
      }
      .stage-tag[data-stage='in'] {
        background: rgba(34, 197, 94, 0.15);
        color: #15803d;
      }
      .stage-tag[data-stage='leaving'] {
        background: rgba(249, 115, 22, 0.15);
        color: #c2410c;
      }
      .day-tag {
        font-size: 0.75rem;
        padding: 0.1rem 0.5rem;
        border-radius: 6px;
        background: var(--surface-ground);
        color: var(--text-color-secondary);
      }
      .day-tag.today {
        background: rgba(59, 130, 246, 0.12);
        color: #1d4ed8;
        font-weight: 700;
      }
      .cafe-upcoming-main .room {
        font-size: 0.8rem;
        color: var(--text-color-secondary);
      }
      .cafe-timer {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        font-size: 0.82rem;
        color: var(--text-color-secondary);
      }
      .cafe-timer-text b {
        color: var(--text-color);
        font-variant-numeric: tabular-nums;
        margin-inline-start: 0.25rem;
      }
      .cafe-battery {
        flex: 1;
        max-width: 14rem;
        height: 0.6rem;
        border-radius: 999px;
        border: 1px solid var(--surface-border);
        background: var(--surface-ground);
        overflow: hidden;
      }
      .cafe-battery-fill {
        display: block;
        height: 100%;
        background: #22c55e;
        transition: width 0.5s linear;
      }
      .cafe-battery[data-level='mid'] .cafe-battery-fill {
        background: #eab308;
      }
      .cafe-battery[data-level='low'] .cafe-battery-fill {
        background: #ef4444;
      }
      .cafe-battery-pct {
        min-width: 2.5rem;
        font-variant-numeric: tabular-nums;
      }
      .cafe-upcoming-main {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.4rem;
      }
      .cafe-upcoming-main .name {
        font-weight: 700;
      }
      .cafe-upcoming-main .sep {
        color: var(--text-color-secondary);
      }
      .cafe-upcoming-main .time {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .cafe-upcoming-main .day {
        font-size: 0.8rem;
        color: var(--text-color-secondary);
      }
      .cafe-upcoming-actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .checked-label {
        color: #16a34a;
        font-size: 0.85rem;
        font-weight: 600;
      }
      .cafe-details > div {
        display: flex;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.5rem 0;
        border-bottom: 1px dashed var(--surface-border);
      }
      .cafe-details > div:last-child {
        border-bottom: none;
      }
      .cafe-details span {
        color: var(--text-color-secondary);
      }
      .cafe-hero-greeting {
        font-size: 0.95rem;
        color: rgba(233, 201, 143, 0.75);
      }
      .cafe-hero-title {
        font-size: 2rem;
        font-weight: 700;
        margin: 0.2rem 0 0.5rem;
      }
      .cafe-hero-date {
        font-size: 0.9rem;
        color: rgba(233, 201, 143, 0.85);
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }
      .cafe-hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.6rem;
        z-index: 1;
      }
      :host ::ng-deep .cafe-btn-gold {
        background: var(--cafe-gold);
        border-color: var(--cafe-gold);
        color: var(--cafe-green-deep);
      }
      :host ::ng-deep .cafe-btn-outline {
        border-color: var(--cafe-gold);
        color: var(--cafe-gold);
      }
      .cafe-card {
        background: var(--surface-card);
        border: 1px solid var(--surface-border);
        border-radius: 16px;
        padding: 1.4rem;
      }
      .cafe-card-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
      }
      .cafe-card-title {
        font-size: 1.1rem;
        font-weight: 700;
      }
      .cafe-card-sub {
        font-size: 0.82rem;
        color: var(--text-color-secondary);
        margin-top: 0.25rem;
      }
      .cafe-stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
        gap: 1rem;
      }
      .cafe-stat {
        display: flex;
        gap: 1rem;
        align-items: flex-start;
      }
      .cafe-stat-icon {
        width: 2.8rem;
        height: 2.8rem;
        flex-shrink: 0;
        border-radius: 12px;
        background: var(--cafe-accent-bg);
        color: var(--cafe-accent-ink);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25rem;
      }
      .cafe-stat-title {
        color: var(--text-color-secondary);
        font-size: 0.88rem;
      }
      .cafe-stat-value {
        font-size: 1.8rem;
        font-weight: 700;
        line-height: 1.3;
      }
      .cafe-stat-hint {
        color: var(--text-color-secondary);
        font-size: 0.78rem;
      }
      .cafe-grid {
        display: grid;
        grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
        gap: 1.5rem;
      }
      @media (max-width: 991px) {
        .cafe-grid {
          grid-template-columns: minmax(0, 1fr);
        }
      }
      .cafe-peak {
        margin: 1rem 0 0.75rem;
        padding: 0.6rem 0.9rem;
        border-radius: 10px;
        background: var(--cafe-accent-bg);
        color: var(--cafe-accent-ink);
        font-size: 0.9rem;
        display: inline-flex;
        gap: 0.4rem;
        align-items: center;
      }
      .cafe-chart-wrap {
        height: 18rem;
      }
      .cafe-legend {
        display: flex;
        gap: 1.25rem;
        font-size: 0.8rem;
        color: var(--text-color-secondary);
        margin-top: 0.75rem;
      }
      .cafe-swatch {
        display: inline-block;
        width: 0.7rem;
        height: 0.7rem;
        border-radius: 3px;
        vertical-align: middle;
        margin-left: 0.3rem;
      }
      .cafe-swatch-peak {
        background: var(--cafe-gold-strong);
      }
      .cafe-swatch-normal {
        background: #2f7d68;
      }
      :host-context(.app-dark) .cafe-swatch-peak {
        background: #e9c98f;
      }
      :host-context(.app-dark) .cafe-swatch-normal {
        background: #4fae8f;
      }
      .cafe-room {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 0;
        border-bottom: 1px dashed var(--surface-border);
      }
      .cafe-room:last-child {
        border-bottom: none;
      }
      .cafe-dot {
        width: 0.65rem;
        height: 0.65rem;
        border-radius: 50%;
        background: #22a06b;
        flex-shrink: 0;
      }
      .cafe-dot.busy {
        background: #d9822b;
      }
      .cafe-timeline {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .cafe-tl-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.75rem 1rem;
        border-radius: 12px;
        border: 1px solid var(--surface-border);
      }
      .cafe-tl-row.now {
        border-color: var(--cafe-gold-strong);
        background: var(--cafe-accent-bg);
      }
      .cafe-tl-row.cancelled {
        opacity: 0.6;
      }
      .cafe-tl-time {
        font-weight: 700;
        min-width: 7.5rem;
        color: var(--cafe-accent-ink);
      }
      .cafe-now-badge {
        font-size: 0.75rem;
        color: var(--cafe-accent-ink);
        font-weight: 700;
      }
      @media (max-width: 575px) {
        .cafe-hero {
          padding: 1.4rem;
        }
        .cafe-tl-row {
          flex-wrap: wrap;
        }
      }
      .cafe-range {
        display: flex;
        flex-direction: column;
        gap: 0.9rem;
      }
      .cafe-range-presets {
        overflow-x: auto;
        padding-bottom: 2px;
      }
      .cafe-presets {
        display: inline-flex;
        border: 1px solid var(--surface-border);
        border-radius: 10px;
        overflow: hidden;
        white-space: nowrap;
      }
      .cafe-preset {
        border: none;
        border-inline-start: 1px solid var(--surface-border);
        background: transparent;
        color: inherit;
        padding: 0.5rem 0.9rem;
        cursor: pointer;
        font-family: inherit;
        font-size: 0.9rem;
      }
      .cafe-preset:first-child {
        border-inline-start: none;
      }
      .cafe-preset.today {
        font-weight: 700;
      }
      .cafe-preset.active {
        background: var(--primary-color);
        color: var(--primary-contrast-color);
      }
      .cafe-range-pick {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.75rem 1.5rem;
      }
      .cafe-range-end {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
      }
      .cafe-range-text {
        color: var(--text-color-secondary);
        font-size: 0.9rem;
      }
      .cafe-range-label {
        font-size: 0.85rem;
        color: var(--text-color-secondary);
      }
      .cafe-status-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 0.75rem;
      }
      .cafe-status {
        display: flex;
        align-items: center;
        gap: 0.8rem;
        padding: 0.9rem 1rem;
        border-radius: 12px;
        border: 1px solid var(--surface-border);
      }
      .cafe-status i {
        font-size: 1.3rem;
      }
      .cafe-status[data-status='pending'] i {
        color: #eab308;
      }
      .cafe-status[data-status='paid'] i {
        color: #22c55e;
      }
      .cafe-status[data-status='confirmed'] i {
        color: #3b82f6;
      }
      .cafe-status[data-status='completed'] i {
        color: #64748b;
      }
      .cafe-status[data-status='cancelled'] i {
        color: #e5484d;
      }
      .cafe-status-value {
        font-size: 1.4rem;
        font-weight: 700;
        line-height: 1.2;
      }
      .cafe-status-label {
        font-size: 0.8rem;
        color: var(--text-color-secondary);
      }
      .cafe-table {
        width: 100%;
        border-collapse: collapse;
      }
      .cafe-table th,
      .cafe-table td {
        text-align: right;
        padding: 0.6rem 0.5rem;
        border-bottom: 1px dashed var(--surface-border);
        white-space: nowrap;
      }
      .cafe-table th {
        font-size: 0.82rem;
        color: var(--text-color-secondary);
        font-weight: 600;
      }
      .cafe-bar {
        display: inline-block;
        width: 6rem;
        height: 0.45rem;
        border-radius: 999px;
        background: var(--surface-border);
        overflow: hidden;
        vertical-align: middle;
        margin-left: 0.5rem;
      }
      .cafe-bar span {
        display: block;
        height: 100%;
        background: var(--cafe-gold-strong);
      }
    </style>
  `,
})
export class CafeDashboardComponent implements OnInit, OnDestroy {
  loading = true;
  faNumber = faNumber;
  faMoney = faMoney;
  capacityLabel = capacityLabel;
  presets = DASHBOARD_PRESETS;

  rooms: Room[] = [];
  reservations: Reservation[] = [];

  greeting = '';
  // رزروهای ساعات پیش رو (زیر سربرگ)
  upcoming: Reservation[] = [];
  details: Reservation | null = null;
  detailsVisible = false;
  checkingId: number | null = null;
  canCheckIn: boolean;
  todayLabel = '';
  rangeLabel = '';
  fromLabel = '';
  toLabel = '';
  isSingleDay = true;
  customFrom: Date | null = null;
  customTo: Date | null = null;

  stats: StatCard[] = [];
  statusCounts: { status: ReservationStatus; label: string; icon: string; count: number }[] = [];
  roomStatuses: RoomStatus[] = [];
  roomBreakdown: RoomBreakdown[] = [];
  rangeReservations: Reservation[] = [];
  listedReservations: Reservation[] = [];

  chartData: any;
  chartOptions: any;
  hasChartData = false;
  peakLabel = '';

  private timer?: ReturnType<typeof setInterval>;
  // تایمرهای «مانده تا شروع/پایان» هر ثانیه
  private tick?: ReturnType<typeof setInterval>;
  nowMs = Date.now();
  readonly checkInEarly = CHECK_IN_EARLY_MINUTES;
  private themeSub: Subscription;
  private liveSub: Subscription;

  constructor(
    private roomsService: RoomsService,
    private reservationsService: ReservationsService,
    private layoutService: LayoutService,
    private rangeService: DashboardRangeService,
    private cdr: ChangeDetectorRef,
    public branding: BrandingService,
    private live: LiveRefreshService,
    private messageService: MessageService,
    private locations: LocationsService,
    auth: AuthService,
  ) {
    this.canCheckIn = auth.can('reservations.edit', 'reservations.create');
    // به‌روزرسانی آنلاین داشبورد با هر رویداد جدید
    this.liveSub = this.live.changes$.subscribe(() => this.reload());
    this.themeSub = this.layoutService.configUpdate$.pipe(debounceTime(25)).subscribe(() => {
      this.buildChart();
      this.cdr.detectChanges();
    });
  }

  get range() {
    return this.rangeService.range;
  }

  ngOnInit() {
    this.rangeService.sync();
    this.syncCustomDates();

    forkJoin({ rooms: this.roomsService.getRooms(), reservations: this.reservationsService.getAll() }).subscribe({
      next: ({ rooms, reservations }) => {
        this.allRooms = rooms.filter((r) => r.isActive !== false);
        this.applyLocation(reservations);
        this.loading = false;
        this.refresh();
      },
      error: () => {
        this.loading = false;
        this.refresh();
      },
    });

    // هر دقیقه وضعیت «همین الان» به‌روز شود
    this.timer = setInterval(() => this.refresh(), 60_000);
    this.tick = setInterval(() => {
      this.nowMs = Date.now();
      this.cdr.detectChanges();
    }, 1000);
  }

  ngOnDestroy() {
    if (this.timer) clearInterval(this.timer);
    if (this.tick) clearInterval(this.tick);
    this.themeSub.unsubscribe();
    this.liveSub.unsubscribe();
  }

  // فیلتر لوکیشن انتخاب‌شده در نوار بالا
  private allRooms: Room[] = [];
  private applyLocation(reservations: Reservation[]) {
    this.rooms = this.locations.filterRooms(this.allRooms);
    const ids = new Set(this.rooms.map((r) => r.id));
    this.reservations = reservations.filter((r) => ids.has(r.roomId));
  }

  private reload() {
    this.reservationsService.getAll().subscribe((reservations) => {
      this.applyLocation(reservations);
      this.refresh();
    });
  }

  // رزروهای لغونشده‌ای که هنوز تمام نشده‌اند (یا وارد شده‌اند و خروجشان ثبت نشده)، به ترتیب ساعت شروع
  private buildUpcoming() {
    const now = Date.now();
    this.upcoming = this.reservations
      .filter((r) => {
        if (r.status === 'cancelled' || r.checkedOutAt) return false;
        const end = new Date(r.endTime).getTime();
        return end > now || (!!r.checkedInAt && end > now - 3 * 3_600_000);
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      .slice(0, 20);
    if (this.details) this.details = this.reservations.find((r) => r.id === this.details!.id) ?? this.details;
  }

  trackById = (_: number, r: Reservation) => r.id;

  started(r: Reservation) {
    return new Date(r.startTime).getTime() <= this.nowMs;
  }

  // ثبت ورود فقط از کمی پیش از شروع تا پایان رزرو
  checkInOpen(r: Reservation) {
    const start = new Date(r.startTime).getTime();
    return this.nowMs >= start - CHECK_IN_EARLY_MINUTES * 60_000 && this.nowMs < new Date(r.endTime).getTime();
  }

  // در انتظار ورود | وارد شده | در انتظار خروج
  stageOf(r: Reservation): 'waiting' | 'in' | 'leaving' {
    if (!r.checkedInAt) return 'waiting';
    const end = new Date(r.endTime).getTime();
    return end - this.nowMs <= LEAVING_MINUTES * 60_000 ? 'leaving' : 'in';
  }

  stageLabel(r: Reservation) {
    return { waiting: 'در انتظار ورود', in: 'وارد شده', leaving: 'در انتظار خروج' }[this.stageOf(r)];
  }

  // ساعت:دقیقه:ثانیه تا شروع (یا تا پایان پس از شروع)
  countdown(r: Reservation) {
    const target = new Date(this.started(r) ? r.endTime : r.startTime).getTime();
    let ms = target - this.nowMs;
    if (ms <= 0) return this.started(r) ? 'زمان تمام شد' : '۰';
    const days = Math.floor(ms / 86_400_000);
    ms -= days * 86_400_000;
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const clock = `${pad(h)}:${pad(m)}:${pad(s)}`.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[+d]);
    return (days ? `${faNumber(days)} روز و ` : '') + clock;
  }

  // پیش از شروع: با نزدیک شدن به شروع پر می‌شود (افق ۳ ساعته)؛ پس از شروع: با گذشت زمان خالی می‌شود
  battery(r: Reservation) {
    const start = new Date(r.startTime).getTime();
    const end = new Date(r.endTime).getTime();
    if (this.nowMs < start) {
      const horizon = 3 * 3_600_000;
      return Math.round(Math.max(0, Math.min(1, 1 - (start - this.nowMs) / horizon)) * 100);
    }
    return Math.round(Math.max(0, Math.min(1, (end - this.nowMs) / Math.max(1, end - start))) * 100);
  }

  batteryLevel(r: Reservation) {
    const v = this.battery(r);
    if (!this.started(r)) return v >= 66 ? 'low' : v >= 33 ? 'mid' : 'high';
    return v > 50 ? 'high' : v > 20 ? 'mid' : 'low';
  }

  dayTag(value: string) {
    const day = new Date(value);
    day.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((day.getTime() - today.getTime()) / 86_400_000);
    if (diff === 0) return 'امروز';
    if (diff === 1) return 'فردا';
    if (diff === -1) return 'دیروز';
    return formatJalali(day, 'dddd D MMMM');
  }

  checkOut(r: Reservation, undo = false) {
    this.checkingId = r.id!;
    this.reservationsService.checkOut(r.id!, undo).subscribe({
      next: (updated) => {
        this.checkingId = null;
        Object.assign(r, { checkedOutAt: updated.checkedOutAt });
        if (this.details && this.details.id === r.id) this.details = { ...this.details, checkedOutAt: updated.checkedOutAt };
        this.messageService.add({ severity: 'success', summary: 'موفق', detail: 'خروج مشتری ثبت شد' });
        this.buildUpcoming();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.checkingId = null;
        this.messageService.add({ severity: 'error', summary: 'خطا', detail: err?.error?.message || 'ثبت خروج ناموفق بود' });
        this.cdr.detectChanges();
      },
    });
  }

  customerOf(r: Reservation) {
    return [r.customerFirstName, r.customerLastName].filter(Boolean).join(' ') || r.customerPhone || '-';
  }

  timeOf(value: string | null | undefined) {
    return value ? faTime(new Date(value)) : '';
  }

  isToday(value: string) {
    return new Date(value).toDateString() === new Date().toDateString();
  }

  dayOf(value: string) {
    return formatJalaliLong(new Date(value));
  }

  methodLabel(m: string) {
    return PAYMENT_METHOD_LABELS[m] ?? m;
  }

  openDetails(r: Reservation) {
    this.details = r;
    this.detailsVisible = true;
  }

  // «ثبت ورود» مشتری
  checkIn(r: Reservation, undo = false) {
    this.checkingId = r.id!;
    this.reservationsService.checkIn(r.id!, undo).subscribe({
      next: (updated) => {
        this.checkingId = null;
        Object.assign(r, { checkedInAt: updated.checkedInAt });
        if (this.details && this.details.id === r.id) this.details = { ...this.details, checkedInAt: updated.checkedInAt };
        this.messageService.add({ severity: 'success', summary: 'موفق', detail: undo ? 'ثبت ورود لغو شد' : 'ورود مشتری ثبت شد' });
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.checkingId = null;
        this.messageService.add({ severity: 'error', summary: 'خطا', detail: err?.error?.message || 'ثبت ورود ناموفق بود' });
        this.cdr.detectChanges();
      },
    });
  }

  onPreset(preset: DashboardPreset) {
    this.rangeService.setPreset(preset);
    this.syncCustomDates();
    this.refresh();
  }

  onCustomChange(from: Date | null, to: Date | null) {
    if (!from && !to) return;
    const current = this.rangeService.dates();
    // انتخاب همان تاریخ فعلی (هنگام همگام‌سازی) بازه را «سفارشی» نمی‌کند
    if ((from && toApiDateKey(from) === toApiDateKey(current.from)) || (to && toApiDateKey(to) === toApiDateKey(current.to))) return;
    const nextFrom = from ?? current.from;
    const nextTo = to ?? current.to;
    this.rangeService.setCustom(toApiDateKey(nextFrom), toApiDateKey(nextTo));
    this.syncCustomDates();
    this.refresh();
  }

  private syncCustomDates() {
    const { from, to } = this.rangeService.dates();
    this.customFrom = from;
    this.customTo = to;
  }

  private refresh() {
    const now = new Date();
    const hour = now.getHours();
    this.greeting = hour >= 4 && hour < 12 ? 'صبح بخیر ☕' : hour >= 12 && hour < 17 ? 'ظهر بخیر' : 'عصر بخیر';
    this.todayLabel = formatJalaliLong(currentBusinessDate());
    this.buildUpcoming();

    const { from, to } = this.range();
    this.isSingleDay = from === to;
    this.fromLabel = this.dayLabelWithRelative(from);
    this.toLabel = this.dayLabelWithRelative(to);
    this.rangeLabel = this.isSingleDay ? this.fromLabel : `${formatJalali(from)} تا ${formatJalali(to)}`;

    this.rangeReservations = this.reservations
      .filter((r) => {
        const key = toApiDateKey(businessDateOf(r.startTime));
        return key >= from && key <= to;
      })
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
    this.listedReservations = this.rangeReservations.slice(0, MAX_LIST_ROWS);

    this.buildStats(dayCount(from, to));
    this.buildRoomStatuses(now);
    this.buildChart();
    this.cdr.detectChanges();
  }

  private buildStats(days: number) {
    const all = this.rangeReservations;
    const active = all.filter((r) => r.status !== 'cancelled');
    const cancelled = all.filter((r) => r.status === 'cancelled');
    const sum = (list: Reservation[]) => list.reduce((s, r) => s + (r.totalPrice ?? 0), 0);

    const earned = sum(active.filter((r) => isRevenue(r.status, r.paymentStatus)));
    const pending = active.filter((r) => r.status === 'pending');
    const pendingRevenue = sum(pending);
    const unpriced = active.filter((r) => r.totalPrice == null).length;

    const totalSlots = this.rooms.reduce((s, room) => s + this.slotCount(room), 0) * days;
    const occupancy = totalSlots ? Math.min(100, Math.round((active.length / totalSlots) * 100)) : 0;
    const guests = active.reduce((s, r) => s + (r.room?.capacity ?? this.roomById(r.roomId)?.capacity ?? 0), 0);
    const cancelRate = all.length ? Math.round((cancelled.length / all.length) * 100) : 0;

    this.stats = [
      {
        title: 'تعداد رزروها',
        value: faNumber(active.length),
        hint: pending.length ? `${faNumber(pending.length)} رزرو در انتظار تایید` : 'همه‌ی رزروها بررسی شده‌اند',
        icon: 'pi pi-calendar-plus',
      },
      {
        title: 'درآمد قطعی',
        value: faMoney(earned),
        hint: pendingRevenue ? `${faMoney(pendingRevenue)} در انتظار تایید` : 'رزروهای تاییدشده و انجام‌شده',
        icon: 'pi pi-wallet',
      },
      {
        title: 'ضریب اشغال',
        value: `${faNumber(occupancy)}٪`,
        hint: `${faNumber(active.length)} از ${faNumber(totalSlots)} بازه‌ی قابل رزرو`,
        icon: 'pi pi-th-large',
      },
      {
        title: 'مهمان‌ها (حداکثر ظرفیت)',
        value: faNumber(guests),
        hint: unpriced ? `${faNumber(unpriced)} رزرو بدون مبلغ` : 'بر اساس ظرفیت اتاق‌های رزروشده',
        icon: 'pi pi-users',
      },
      {
        title: 'رزروهای لغوشده',
        value: faNumber(cancelled.length),
        hint: all.length ? `${faNumber(cancelRate)}٪ از رزروهای بازه` : 'لغوی نداشتیم',
        icon: 'pi pi-times-circle',
      },
    ];

    this.statusCounts = STATUS_ORDER.map((status) => ({
      status,
      label: STATUS_LABELS[status],
      icon: STATUS_ICONS[status],
      count: all.filter((r) => r.status === status).length,
    }));

    this.roomBreakdown = this.rooms.map((room) => {
      const roomActive = active.filter((r) => r.roomId === room.id);
      const slots = this.slotCount(room) * days;
      return {
        room,
        count: roomActive.length,
        revenue: sum(roomActive.filter((r) => isRevenue(r.status, r.paymentStatus))),
        occupancy: slots ? Math.min(100, Math.round((roomActive.length / slots) * 100)) : 0,
      };
    });
  }

  private buildRoomStatuses(now: Date) {
    const active = this.reservations.filter((r) => r.status !== 'cancelled');

    this.roomStatuses = this.rooms.map((room) => {
      const roomRes = active.filter((r) => r.roomId === room.id);
      const current = roomRes.find((r) => this.isNow(r, now));
      if (current) {
        return { room, busyNow: true, text: `اشغال تا ساعت ${faTime(new Date(current.endTime))}` };
      }
      const next = roomRes
        .filter((r) => new Date(r.startTime) > now)
        .sort((a, b) => a.startTime.localeCompare(b.startTime))[0];
      const nextIsToday =
        next && toApiDateKey(businessDateOf(next.startTime)) === toApiDateKey(currentBusinessDate());
      return {
        room,
        busyNow: false,
        text: nextIsToday ? `آزاد · رزرو بعدی ${faTime(new Date(next.startTime))}` : 'آزاد · امروز رزرو دیگری ندارد',
      };
    });
  }

  private buildChart() {
    const counts = this.busyHours();
    const values = BUSINESS_HOURS.map((h) => counts[h] ?? 0);
    this.hasChartData = values.some((v) => v > 0);

    const peakHours = [...BUSINESS_HOURS]
      .sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0))
      .slice(0, PEAK_HOURS_COUNT)
      .filter((h) => (counts[h] ?? 0) > 0);
    const peakSet = new Set(peakHours);
    const peakOrdered = BUSINESS_HOURS.filter((h) => peakSet.has(h));
    const isContiguous = peakOrdered.every(
      (h, i) => i === 0 || BUSINESS_HOURS.indexOf(h) === BUSINESS_HOURS.indexOf(peakOrdered[i - 1]) + 1,
    );
    this.peakLabel = !peakOrdered.length
      ? '-'
      : isContiguous
        ? `${hourLabel(peakOrdered[0])} تا ${hourLabel((peakOrdered[peakOrdered.length - 1] + 1) % 24)}`
        : peakOrdered.map(hourLabel).join('، ');

    const dark = !!this.layoutService.isDarkTheme();
    const normal = dark ? '#4fae8f' : '#2f7d68';
    const peak = dark ? '#e9c98f' : '#b8862b';
    const style = getComputedStyle(document.documentElement);
    const textMuted = style.getPropertyValue('--text-color-secondary');
    const border = style.getPropertyValue('--surface-border');

    this.chartData = {
      labels: BUSINESS_HOURS.map(hourLabel),
      datasets: [
        {
          label: 'تعداد رزرو',
          data: values,
          backgroundColor: BUSINESS_HOURS.map((h) => (peakSet.has(h) ? peak : normal)),
          borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
          maxBarThickness: 22,
        },
      ],
    };

    this.chartOptions = {
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          rtl: true,
          callbacks: {
            title: (items: any[]) => {
              const h = BUSINESS_HOURS[items[0].dataIndex];
              return `${hourLabel(h)} تا ${hourLabel((h + 1) % 24)}`;
            },
            label: (item: any) => `${faNumber(item.raw)} رزرو`,
          },
        },
      },
      scales: {
        x: {
          reverse: true,
          ticks: { color: textMuted, maxRotation: 0, autoSkip: true },
          grid: { display: false },
        },
        y: {
          position: 'right',
          beginAtZero: true,
          ticks: { color: textMuted, precision: 0, callback: (v: number) => faNumber(v) },
          grid: { color: border, drawTicks: false },
          border: { display: false },
        },
      },
    };
  }

  // شمارش رزروهای فعال هر ساعت در بازه‌ی انتخابی (هر ساعتی که رزرو پوشش می‌دهد)
  private busyHours(): Record<number, number> {
    const counts: Record<number, number> = {};
    this.rangeReservations
      .filter((r) => r.status !== 'cancelled')
      .forEach((r) => {
        const start = new Date(r.startTime);
        const end = new Date(r.endTime);
        for (let t = new Date(start); t < end; t.setHours(t.getHours() + 1, 0, 0, 0)) {
          const h = t.getHours();
          counts[h] = (counts[h] || 0) + 1;
        }
      });
    return counts;
  }

  private slotCount(room: Room): number {
    const slots = (room.bookableGroups || []).filter((g) => g.isActive !== false).flatMap((g) => g.slots || []);
    return new Set(slots.map((s) => `${s.startTime}-${s.endTime}`)).size;
  }

  private roomById(id: number): Room | undefined {
    return this.rooms.find((r) => r.id === id);
  }

  roomName(id: number): string {
    return this.roomById(id)?.name || '-';
  }

  isNow(r: Reservation, now = new Date()): boolean {
    return r.status !== 'cancelled' && new Date(r.startTime) <= now && now < new Date(r.endTime);
  }

  timeRange(r: Reservation): string {
    return `${faTime(new Date(r.startTime))} تا ${faTime(new Date(r.endTime))}`;
  }

  // «امروز · چهارشنبه ۱۵ مهر ۱۴۰۵»
  private dayLabelWithRelative(key: string): string {
    const today = currentBusinessDate();
    const diff = Math.round((fromApiDateKey(key).getTime() - today.getTime()) / 86_400_000);
    const relative = diff === 0 ? 'امروز' : diff === -1 ? 'دیروز' : diff === 1 ? 'فردا' : '';
    return [relative, formatJalaliLong(fromApiDateKey(key))].filter(Boolean).join(' · ');
  }

  dayLabel(r: Reservation): string {
    return formatJalali(businessDateOf(r.startTime), 'dddd D MMMM');
  }

  customerLabel(r: Reservation): string {
    const fullName = [r.customerFirstName, r.customerLastName].filter(Boolean).join(' ');
    const parts = [fullName || (r.user ? `${r.user.firstName} ${r.user.lastName}` : ''), r.customerPhone].filter(Boolean);
    return parts.join(' · ') || 'مشتری بدون نام';
  }

  statusLabel(status?: ReservationStatus): string {
    return status ? STATUS_LABELS[status] : '';
  }

  statusSeverity(status?: ReservationStatus) {
    return status ? STATUS_SEVERITY[status] : 'info';
  }
}
